import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import Keyboard from "react-simple-keyboard";
import { Box, InputAdornment, TextField } from "@mui/material";

import Modal from "@components/modal";

import {
  KEYBOARD_LAYOUT,
  KEYBOARD_DISPLAY,
  KEYBOARD_BUTTON_THEME,
  NUMPAD_LAYOUT,
  NUMPAD_BUTTON_THEME,
  getDisabledButtons,
} from "./screenKeyboard.config";
import { ScreenKeyboardVariant } from "./screenKeyboard.types";
import {
  rootContainerStyles,
  contentWrapperStyles,
  previewWrapperStyles,
  previewInputStyles,
  phonePrefixAdornmentStyles,
  suggestionsWrapperStyles,
  suggestionChipStyles,
  keyboardWrapperStyles,
  numpadWrapperStyles,
  footerStyles,
  cancelButtonStyles,
  enterButtonStyles,
} from "./screenKeyboard.styles";

const MAX_SUGGESTIONS = 3;

const PHONE_PREFIX = "+";
const DIGIT_PATTERN = /^[0-9]$/;

interface ISimpleKeyboardInstance {
  setInput: (value: string) => void;
  getCaretPosition: () => number | null;
  setCaretPosition: (
    position: number | null,
    endPosition?: number | null
  ) => void;
}

interface IScreenKeyboardModalProps {
  value: string;
  onChange: (value: string) => void;
  onCancel: VoidFunction;
  onEnter: VoidFunction;
  variant?: ScreenKeyboardVariant;
  pattern?: RegExp | null;
  shiftFirst?: boolean;
  suggestions?: string[] | null;
}

const ScreenKeyboardModal: FC<IScreenKeyboardModalProps> = ({
  value,
  onChange,
  onCancel,
  onEnter,
  variant = "default",
  pattern,
  shiftFirst = false,
  suggestions,
}) => {
  const isPhoneNumpad = variant === "phone-numpad";
  const isNumeric = variant === "numpad" || isPhoneNumpad;
  const displayValue = isPhoneNumpad ? value.replace(/\+/g, "") : value;
  let allowedPattern: RegExp | null = null;
  if (variant === "regex") {
    allowedPattern = pattern ?? null;
  } else if (isNumeric) {
    allowedPattern = DIGIT_PATTERN;
  }
  const [layoutName, setLayoutName] = useState<string>(
    !isNumeric && shiftFirst ? "shift" : "default"
  );
  const [caretPosition, setCaretPositionState] = useState<number>(
    displayValue.length
  );
  const keyboardRef = useRef<ISimpleKeyboardInstance | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingCaretPositionRef = useRef<number | null>(null);
  const isShiftLockedRef = useRef<boolean>(false);
  const suppressNextChangeRef = useRef<boolean>(false);

  const layout = isNumeric ? NUMPAD_LAYOUT : KEYBOARD_LAYOUT;

  const filteredSuggestions = useMemo(() => {
    if (!suggestions?.length) {
      return [];
    }
    const query = displayValue.trim().toLowerCase();
    const matches = query
      ? suggestions.filter((item) => item.toLowerCase().includes(query))
      : suggestions;
    return matches.slice(0, MAX_SUGGESTIONS);
  }, [suggestions, displayValue]);

  const disabledArrowButtons = useMemo(() => {
    const disabled: string[] = [];
    if (caretPosition <= 0) {
      disabled.push("{arrowleft}");
    }
    if (caretPosition >= displayValue.length) {
      disabled.push("{arrowright}");
    }
    return disabled.join(" ");
  }, [caretPosition, displayValue.length]);

  const patternDisabledButtons = useMemo(
    () =>
      isNumeric ? "" : getDisabledButtons(KEYBOARD_LAYOUT, allowedPattern),
    [isNumeric, allowedPattern]
  );

  const buttonTheme = useMemo(() => {
    const base = isNumeric ? NUMPAD_BUTTON_THEME : KEYBOARD_BUTTON_THEME;
    const disabledButtons = [patternDisabledButtons, disabledArrowButtons]
      .filter(Boolean)
      .join(" ");
    let theme = disabledButtons
      ? [...base, { class: "hg-key-disabled", buttons: disabledButtons }]
      : base;
    if (!isNumeric && layoutName === "shift") {
      theme = [...theme, { class: "hg-key-accent", buttons: "{shift}" }];
    }
    return theme;
  }, [isNumeric, patternDisabledButtons, layoutName, disabledArrowButtons]);

  useEffect(() => {
    keyboardRef.current?.setInput(displayValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pendingCaretPositionRef.current === null) {
      return;
    }
    const position = pendingCaretPositionRef.current;
    inputRef.current?.setSelectionRange(position, position);
    keyboardRef.current?.setCaretPosition(position, position);
    pendingCaretPositionRef.current = null;
  }, [displayValue]);

  const commitValue = (
    nextValue: string,
    caretHint: number | null,
    skipValidation = false
  ) => {
    const cleanValue = nextValue.replace(/\n/g, "");
    const hasDisallowedChar =
      !skipValidation &&
      !!allowedPattern &&
      [...cleanValue].some((char) => !allowedPattern.test(char));

    if (hasDisallowedChar) {
      keyboardRef.current?.setInput(displayValue);
      if (inputRef.current) {
        inputRef.current.value = displayValue;
        inputRef.current.setSelectionRange(caretPosition, caretPosition);
      }
      return;
    }

    keyboardRef.current?.setInput(cleanValue);
    const nextCaretPosition = Math.min(
      caretHint ?? cleanValue.length,
      cleanValue.length
    );
    pendingCaretPositionRef.current = nextCaretPosition;
    setCaretPositionState(nextCaretPosition);
    onChange(
      isPhoneNumpad && cleanValue.length > 0
        ? PHONE_PREFIX + cleanValue
        : cleanValue
    );
  };

  const handleKeyboardChange = (nextValue: string) => {
    if (suppressNextChangeRef.current) {
      suppressNextChangeRef.current = false;
      return;
    }
    commitValue(nextValue, keyboardRef.current?.getCaretPosition() ?? null);
  };

  const handlePhysicalKeyboardChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    commitValue(e.target.value, e.target.selectionStart);
  };

  const handleSuggestionSelect = (suggestion: string) => {
    commitValue(suggestion, suggestion.length, true);
  };

  const handleInputBlur = () => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleSelectionChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const nextPosition = target.selectionStart ?? displayValue.length;
    keyboardRef.current?.setCaretPosition(nextPosition, target.selectionEnd);
    setCaretPositionState(nextPosition);
  };

  const handleKeyPress = (button: string) => {
    if (button === "{shift}") {
      setLayoutName((prev) => {
        const next = prev === "shift" ? "default" : "shift";
        isShiftLockedRef.current = next === "shift";
        return next;
      });
      return;
    }
    if (button === "{numbers}") {
      setLayoutName("numbers");
      return;
    }
    if (button === "{abc}") {
      setLayoutName("default");
      return;
    }
    if (button === "{clear}") {
      commitValue("", 0, true);
      return;
    }
    if (button === "{bksp}") {
      if (caretPosition <= 0) {
        return;
      }
      const newValue =
        displayValue.slice(0, caretPosition - 1) +
        displayValue.slice(caretPosition);
      suppressNextChangeRef.current = true;
      commitValue(newValue, caretPosition - 1, true);
      return;
    }
    if (button === "{arrowleft}" || button === "{arrowright}") {
      const nextPosition =
        button === "{arrowleft}"
          ? Math.max(0, caretPosition - 1)
          : Math.min(displayValue.length, caretPosition + 1);
      inputRef.current?.setSelectionRange(nextPosition, nextPosition);
      inputRef.current?.focus();
      keyboardRef.current?.setCaretPosition(nextPosition, nextPosition);
      setCaretPositionState(nextPosition);
      return;
    }
    if (!button.startsWith("{")) {
      setLayoutName((prev) =>
        prev === "shift" && !isShiftLockedRef.current ? "default" : prev
      );
    }
  };

  return (
    <Modal disableBodyScroll topmost>
      <Box sx={rootContainerStyles}>
        <Box sx={contentWrapperStyles}>
          <Box sx={previewWrapperStyles}>
            <TextField
              value={displayValue}
              onChange={handlePhysicalKeyboardChange}
              onBlur={handleInputBlur}
              inputRef={inputRef}
              fullWidth
              autoFocus
              variant="outlined"
              sx={previewInputStyles}
              slotProps={{
                input: {
                  startAdornment: isPhoneNumpad ? (
                    <InputAdornment position="start" disableTypography>
                      <Box component="span" sx={phonePrefixAdornmentStyles}>
                        {PHONE_PREFIX}
                      </Box>
                    </InputAdornment>
                  ) : undefined,
                },
                htmlInput: {
                  onClick: handleSelectionChange,
                  onKeyUp: handleSelectionChange,
                },
              }}
            />
          </Box>

          {!!filteredSuggestions.length && (
            <Box
              sx={suggestionsWrapperStyles}
              onPointerDown={(e) => e.preventDefault()}
            >
              {filteredSuggestions.map((suggestion) => (
                <Box
                  key={suggestion}
                  sx={suggestionChipStyles}
                  onClick={() => handleSuggestionSelect(suggestion)}
                >
                  {suggestion}
                </Box>
              ))}
            </Box>
          )}

          <Box
            sx={isNumeric ? numpadWrapperStyles : keyboardWrapperStyles}
            onPointerDown={(e) => e.preventDefault()}
          >
            <Keyboard
              keyboardRef={(r) => {
                keyboardRef.current = r;
              }}
              onChange={handleKeyboardChange}
              onKeyPress={handleKeyPress}
              layout={layout}
              layoutName={layoutName}
              display={KEYBOARD_DISPLAY}
              buttonTheme={buttonTheme}
            />
          </Box>

          <Box sx={footerStyles}>
            <Box sx={cancelButtonStyles} onClick={onCancel}>
              CANCEL
            </Box>
            <Box sx={enterButtonStyles} onClick={onEnter}>
              ENTER
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ScreenKeyboardModal;
